import React, { useEffect, useMemo, useState, useRef } from 'react';
import Swal from 'sweetalert2';
import { API_BASE_URL } from '../config/env';
import { useAuth } from '../context/AuthContext';
import CenteredMessage from '../components/CenteredMessage';
import { buildCrmSwalBody, fireConfirmDialog } from '../utils/swalConfirm';
import {
  loadAtencionPedidosForFarmacia,
  isAtencionFarmaciaPedido,
  resolveAtencionPedidoId,
  updateAtencionPedidoFarmaciaEstado
} from '../api/atencionPedidos';
import '../styles/gestion-derivacion-premium.css';
import '../styles/farmacia-premium.css';
import '../styles/dispensa-premium.css';
import '../styles/cadeteria-premium.css';
const DeliveryMap = React.lazy(() => import('../components/DeliveryMap'));

const CADETERIA_KANBAN_COLUMNS = [
  { key: 'en_camino', title: 'En camino', subtitle: 'Repartos activos', dropEnabled: false },
  { key: 'entregado', title: 'Entregados', subtitle: 'Confirmar entrega', dropEnabled: true },
  { key: 'cancelado', title: 'Cancelados', subtitle: '', dropEnabled: false }
];

function isCadeteriaReadonlyColumn(colKey) {
  return colKey === 'cancelado';
}

/** Combina pedidos clásicos de farmacia con los de bot mostrador (atención), evitando duplicados. */
function mergeCadeteriaPedidos(classicRows, atencionRows) {
  const classic = Array.isArray(classicRows) ? classicRows : [];
  const atencion = Array.isArray(atencionRows) ? atencionRows : [];
  const seen = new Set(classic.map((p) => String(p?.id)));
  const extra = atencion.filter((p) => !seen.has(String(p?.id)));
  return [...classic, ...extra];
}

function isCadeteriaDropZoneColumn(colKey) {
  return colKey === 'entregado';
}

function getCadeteriaColumnMeta(colKey) {
  if (colKey === 'entregado') {
    return {
      icon: '✅',
      title: 'Soltá acá para confirmar entrega',
      hint: 'Arrastrá pedidos desde En camino cuando el cadete entregó al paciente.',
      empty: 'Sin entregas registradas',
      emptyHint: 'Las entregas confirmadas aparecen acá',
      ariaTitle: 'Zona de entrega: arrastrá pedidos desde En camino.'
    };
  }
  return {
    icon: '📋',
    title: 'Consulta de cancelaciones',
    hint: 'Los pedidos cancelados se registran en el sistema. No se pueden mover tarjetas acá.',
    empty: 'Sin cancelaciones',
    emptyHint: 'No hay pedidos cancelados en el período',
    ariaTitle: 'Solo lectura. No se pueden mover pedidos a esta columna.'
  };
}

function cadeteriaEstadoLabel(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'entregado') return 'Entregado';
  if (e === 'cancelado') return 'Cancelado';
  if (e === 'en_camino') return 'En camino';
  return estado || '—';
}

function cadeteriaEstadoStatusStyle(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'cancelado') {
    return { background: '#FEF2F2', color: '#B91C1C', border: '1px solid #FECACA' };
  }
  if (e === 'entregado') {
    return { background: '#E5F8F6', color: '#00796B', border: '1px solid #BDE9E4' };
  }
  return { background: '#EEF4FB', color: '#1C4F86', border: '1px solid #D4E1F1' };
}

function isPedidoEstadoTerminal(estado) {
  const e = String(estado || '').toLowerCase();
  return e === 'entregado' || e === 'cancelado';
}

function getFinalizadoCardVariant(estado) {
  return String(estado || '').toLowerCase() === 'cancelado' ? 'cancelado' : 'entregado';
}

function getCadeteriaFinalizadoStampLabel(estado) {
  const e = String(estado || '').toLowerCase();
  if (e === 'cancelado') return 'Anul.';
  if (e === 'entregado') return 'Entreg.';
  return 'Ok';
}

const MED_CATEGORY_RULES = [
  { key: 'hipertension', label: 'Hipertensión', emoji: '🫀', tint: '#fee2e2', text: '#b91c1c', keywords: ['enalapril', 'losartan', 'amlodipina', 'valsartan', 'atenolol', 'bisoprolol', 'carvedilol', 'hidroclorotiazida'] },
  { key: 'diabetes', label: 'Diabetes', emoji: '🩸', tint: '#dbeafe', text: '#1d4ed8', keywords: ['metformina', 'insulina', 'glibenclamida', 'dapagliflozina', 'empagliflozina', 'sitagliptina', 'linagliptina'] },
  { key: 'endocrino', label: 'Endocrino', emoji: '🦋', tint: '#fae8ff', text: '#86198f', keywords: ['levotiroxina', 'tiroides', 'eutirox'] },
  { key: 'celiaquia', label: 'Celiaquia', emoji: '🌾', tint: '#fef3c7', text: '#92400e', keywords: ['sin tacc', 'celiaco', 'celiaca', 'gluten free'] },
  { key: 'respiratorio', label: 'Respiratorio', emoji: '🫁', tint: '#dcfce7', text: '#166534', keywords: ['salbutamol', 'budesonida', 'fluticasona', 'montelukast', 'formoterol', 'tiotropio'] },
  { key: 'dolor', label: 'Dolor crónico', emoji: '💊', tint: '#ede9fe', text: '#6d28d9', keywords: ['pregabalina', 'gabapentina', 'tramadol', 'naproxeno', 'diclofenac', 'ibuprofeno', 'paracetamol'] }
];

function inferMedicationCategory(rawMedication) {
  const med = String(rawMedication || '').toLowerCase();
  for (const rule of MED_CATEGORY_RULES) {
    if (rule.keywords.some((k) => med.includes(k))) return rule;
  }
  return { key: 'general', label: 'Tratamiento crónico', emoji: '🏥', tint: '#e2e8f0', text: '#334155' };
}
function getTodayLocalISO() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function Pedidos() {
  const { user, permissionsLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const firstLoadRef = useRef(true);
  const [items, setItems] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const detailPanelRef = React.useRef(null);
  const [mapDrawerOpen, setMapDrawerOpen] = useState(false);
  const [mapDrawerData, setMapDrawerData] = useState({
    paciente: '',
    direccion: '',
    medicamento: '',
    laboratorio: '',
    latitud: null,
    longitud: null,
    nroPedido: ''
  });
  const [viewportHeight, setViewportHeight] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 900
  );
  const [viewportWidth, setViewportWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1366
  );
  const [historialGlobalOpen, setHistorialGlobalOpen] = useState(false);
  const [historialGlobalDni, setHistorialGlobalDni] = useState('');
  const [historialGlobalPacienteSearch, setHistorialGlobalPacienteSearch] = useState('');
  const [historialGlobalSearch, setHistorialGlobalSearch] = useState('');
  const [historialGlobalDesde, setHistorialGlobalDesde] = useState('');
  const [historialGlobalHasta, setHistorialGlobalHasta] = useState('');
  const [historialGlobalOrder, setHistorialGlobalOrder] = useState('fecha_desc');
  const [historialGlobalPage, setHistorialGlobalPage] = useState(1);
  const [historialGlobalItemsPerPage, setHistorialGlobalItemsPerPage] = useState(6);
  const [search, setSearch] = useState('');
  const [filtroDesde, setFiltroDesde] = useState('');
  const [filtroHasta, setFiltroHasta] = useState('');
  const [filtroRangoModo, setFiltroRangoModo] = useState('hoy_anteriores');
  const [draggingPedidoId, setDraggingPedidoId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState('');
  const [pendienteInfoDrawerOpen, setPendienteInfoDrawerOpen] = useState(false);
  const [pendienteInfoPedido, setPendienteInfoPedido] = useState(null);
  const isTabletOrLess = viewportWidth <= 991;

  const hasPermission = user && Array.isArray(user.permissions)
    && (user.permissions.includes('ver_pedidos') || user.permissions.includes('ver_pedidos_dispensa'));

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!mounted) return;
      const isFirstLoad = firstLoadRef.current;
      if (isFirstLoad) setLoading(true);
      try {
        let fetched = [];
        const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos_farmacia`, { credentials: 'include' });
        if (r.ok) {
          const j = await r.json();
          fetched = Array.isArray(j) ? j : (Array.isArray(j?.pedidos) ? j.pedidos : []);
        }

        let atencionRows = [];
        try {
          atencionRows = await loadAtencionPedidosForFarmacia({ limit: 150 });
        } catch (_) {
          // si falla, seguimos solo con los pedidos clásicos
        }

        if (mounted) {
          setItems(mergeCadeteriaPedidos(fetched, atencionRows));
          setUpdatedAt(new Date());
        }
      } catch (e) {
        if (mounted) setItems([]);
      } finally {
        if (mounted) {
          if (isFirstLoad) setLoading(false);
          firstLoadRef.current = false;
        }
      }
    };

    load();
    const timer = setInterval(load, 15000);
    return () => {
      mounted = false;
      clearInterval(timer);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => {
      setViewportHeight(window.innerHeight || 900);
      setViewportWidth(window.innerWidth || 1366);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const enCaminoItems = useMemo(() => {
    return (items || [])
      .filter((p) => String(p?.estado || '').toLowerCase() === 'en_camino')
      .sort((a, b) => {
        const ta = new Date(a?.fecha || 0).getTime() || 0;
        const tb = new Date(b?.fecha || 0).getTime() || 0;
        return tb - ta;
      });
  }, [items]);
  const filteredEnCaminoItems = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const toMs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    const todayIso = getTodayLocalISO();
    const desdeMs = toMs(filtroDesde || todayIso);
    const hastaMs = toMs(filtroHasta || todayIso);
    return enCaminoItems.filter((p) => {
      const matchText = !q || (
        String(p.paciente || '').toLowerCase().includes(q)
        || String(p.cliente_dni || '').toLowerCase().includes(q)
        || String(p.medicamento || '').toLowerCase().includes(q)
        || String(p.nro_pedido || '').toLowerCase().includes(q)
        || String(p.direccion || '').toLowerCase().includes(q)
      );
      if (!matchText) return false;
      if (filtroRangoModo === 'hoy_anteriores' && isPedidoPendienteAnterior(p)) return true;
      const ts = getPedidoTimestamp(p);
      if (ts == null) return true;
      if (desdeMs && ts < desdeMs) return false;
      if (hastaMs && ts > (hastaMs + 24 * 60 * 60 * 1000 - 1)) return false;
      return true;
    });
  }, [enCaminoItems, search, filtroDesde, filtroHasta, filtroRangoModo]);
  const deliveredItems = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const toMs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    const todayIso = getTodayLocalISO();
    const desdeMs = toMs(filtroDesde || todayIso);
    const hastaMs = toMs(filtroHasta || todayIso);
    const base = (items || []).filter((p) => p?.estado === 'entregado');
    return base.filter((p) => {
      const matchText = !q || (
        String(p.paciente || '').toLowerCase().includes(q)
        || String(p.cliente_dni || '').toLowerCase().includes(q)
        || String(p.medicamento || '').toLowerCase().includes(q)
        || String(p.nro_pedido || '').toLowerCase().includes(q)
        || String(p.direccion || '').toLowerCase().includes(q)
      );
      if (!matchText) return false;
      if (filtroRangoModo === 'hoy_anteriores' && !filtroDesde && !filtroHasta) return true;
      const ts = getPedidoTimestamp(p);
      if (ts == null) return true;
      if (desdeMs && ts < desdeMs) return false;
      if (hastaMs && ts > (hastaMs + 24 * 60 * 60 * 1000 - 1)) return false;
      return true;
    });
  }, [items, search, filtroDesde, filtroHasta, filtroRangoModo]);
  const cancelledItems = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    const toMs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    const todayIso = getTodayLocalISO();
    const desdeMs = toMs(filtroDesde || todayIso);
    const hastaMs = toMs(filtroHasta || todayIso);
    const base = (items || []).filter((p) => p?.estado === 'cancelado');
    return base.filter((p) => {
      const matchText = !q || (
        String(p.paciente || '').toLowerCase().includes(q)
        || String(p.cliente_dni || '').toLowerCase().includes(q)
        || String(p.medicamento || '').toLowerCase().includes(q)
        || String(p.nro_pedido || '').toLowerCase().includes(q)
        || String(p.direccion || '').toLowerCase().includes(q)
      );
      if (!matchText) return false;
      if (filtroRangoModo === 'hoy_anteriores' && !filtroDesde && !filtroHasta) return true;
      const ts = getPedidoTimestamp(p);
      if (ts == null) return true;
      if (desdeMs && ts < desdeMs) return false;
      if (hastaMs && ts > (hastaMs + 24 * 60 * 60 * 1000 - 1)) return false;
      return true;
    });
  }, [items, search, filtroDesde, filtroHasta, filtroRangoModo]);

  const pedidosHistorial = useMemo(() => {
    return (items || []).filter((p) => p?.estado === 'entregado' || p?.estado === 'cancelado');
  }, [items]);

  const getPedidoDni = (pedido) => String(pedido?.cliente_dni || pedido?.paciente_dni || pedido?.dni || '').replace(/\D/g, '');
  const getPedidoPacienteDisplay = (pedido) => String(pedido?.paciente || pedido?.paciente_nombre || '').trim() || `DNI ${getPedidoDni(pedido)}`;
  function getPedidoTimestamp(pedido) {
    const raw = pedido?.fecha || pedido?.updated_at || pedido?.created_at || null;
    if (!raw) return null;
    const source = String(raw);
    const normalized = source.replace(/(\.\d{3})\d+/, '$1').replace(' ', 'T');
    let d = new Date(normalized);
    if (Number.isNaN(d.getTime())) d = new Date(source);
    if (Number.isNaN(d.getTime())) return null;
    return d.getTime();
  }
  function isPedidoPendienteAnterior(pedido) {
    if (!pedido) return false;
    const estado = String(pedido?.estado || '').toLowerCase();
    if (estado === 'entregado' || estado === 'cancelado') return false;
    const ts = getPedidoTimestamp(pedido);
    if (ts == null) return false;
    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);
    return ts < todayStart.getTime();
  }
  function formatFechaSoloDia(raw) {
    if (!raw) return '—';
    const source = String(raw);
    const normalized = source.replace(/(\.\d{3})\d+/, '$1').replace(' ', 'T');
    let d = new Date(normalized);
    if (Number.isNaN(d.getTime())) d = new Date(source);
    if (Number.isNaN(d.getTime())) return source;
    d = new Date(d.getTime() - (3 * 60 * 60 * 1000));
    const months = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
    const day = d.getUTCDate();
    const month = months[d.getUTCMonth()] || '';
    const monthCap = month.charAt(0).toUpperCase() + month.slice(1);
    return `${day} de ${monthCap}`;
  }

  const historialPacientes = useMemo(() => {
    const map = new Map();
    pedidosHistorial.forEach((p) => {
      const dni = getPedidoDni(p);
      if (!dni) return;
      const nombre = getPedidoPacienteDisplay(p);
      if (!map.has(dni)) map.set(dni, { dni, nombre });
    });
    return Array.from(map.values()).sort((a, b) => String(a.nombre).localeCompare(String(b.nombre), 'es', { sensitivity: 'base' }));
  }, [pedidosHistorial]);

  const filteredHistorialPacientes = useMemo(() => {
    const q = String(historialGlobalPacienteSearch || '').trim().toLowerCase();
    if (!q) return historialPacientes;
    return historialPacientes.filter((p) => String(p.nombre || '').toLowerCase().includes(q) || String(p.dni || '').includes(q));
  }, [historialPacientes, historialGlobalPacienteSearch]);

  const historialGlobalItems = useMemo(() => {
    if (!historialGlobalDni) return [];
    return pedidosHistorial.filter((p) => getPedidoDni(p) === String(historialGlobalDni));
  }, [pedidosHistorial, historialGlobalDni]);

  const filteredHistorialGlobalItems = useMemo(() => {
    const q = String(historialGlobalSearch || '').trim().toLowerCase();
    const toMs = (v) => {
      if (!v) return null;
      const d = new Date(v);
      return Number.isNaN(d.getTime()) ? null : d.getTime();
    };
    const desdeMs = toMs(historialGlobalDesde);
    const hastaMs = toMs(historialGlobalHasta);
    let rows = historialGlobalItems.filter((p) => {
      if (!q) return true;
      return (
        String(getPedidoPacienteDisplay(p) || '').toLowerCase().includes(q)
        || String(p.medicamento || '').toLowerCase().includes(q)
        || String(p.nro_pedido || '').toLowerCase().includes(q)
        || String(p.direccion || '').toLowerCase().includes(q)
      );
    });
    rows = rows.filter((p) => {
      const ts = getPedidoTimestamp(p);
      if (ts == null) return true;
      if (desdeMs && ts < desdeMs) return false;
      if (hastaMs && ts > (hastaMs + 24 * 60 * 60 * 1000 - 1)) return false;
      return true;
    });
    rows.sort((a, b) => {
      const aTs = getPedidoTimestamp(a) || 0;
      const bTs = getPedidoTimestamp(b) || 0;
      if (historialGlobalOrder === 'fecha_asc') return aTs - bTs;
      return bTs - aTs;
    });
    return rows;
  }, [historialGlobalItems, historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalOrder]);

  const totalHistorialGlobalPages = Math.max(1, Math.ceil(filteredHistorialGlobalItems.length / historialGlobalItemsPerPage));
  useEffect(() => {
    if (historialGlobalPage > totalHistorialGlobalPages) setHistorialGlobalPage(1);
  }, [historialGlobalPage, totalHistorialGlobalPages]);
  useEffect(() => {
    setHistorialGlobalPage(1);
  }, [historialGlobalSearch, historialGlobalDesde, historialGlobalHasta, historialGlobalOrder, historialGlobalDni, historialGlobalItemsPerPage]);
  const pageHistorialGlobalItems = useMemo(() => (
    filteredHistorialGlobalItems.slice((historialGlobalPage - 1) * historialGlobalItemsPerPage, historialGlobalPage * historialGlobalItemsPerPage)
  ), [filteredHistorialGlobalItems, historialGlobalPage, historialGlobalItemsPerPage]);

  const kpi = useMemo(() => {
    return {
      enCamino: filteredEnCaminoItems.length,
      entregadosHoy: deliveredItems.length,
      cancelados: cancelledItems.length
    };
  }, [filteredEnCaminoItems, deliveredItems, cancelledItems]);

  const pageItems = filteredEnCaminoItems;

  useEffect(() => {
    if (!enCaminoItems.length) {
      setSelectedId(null);
      return;
    }
    if (selectedId && !enCaminoItems.some((p) => String(p.id) === String(selectedId))) {
      setSelectedId(null);
    }
  }, [enCaminoItems, selectedId]);

  const handleSelectPedido = (id) => {
    setSelectedId(id);
    setTimeout(() => {
      try {
        detailPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_) {
        // ignore
      }
    }, 70);
  };

  const getMedicamentoCompleto = (pedido) => {
    const generic = String(pedido?.nombre_generico || pedido?.nombre_generico_med || '').trim();
    const concentration = String(pedido?.concentracion || pedido?.presentacion || '').trim();
    if (generic) return `${generic}${concentration ? ` · ${concentration}` : ''}`;
    const named = String(pedido?.medicamento_nombre || '').trim();
    if (named) return named;
    return String(pedido?.medicamento || '').trim();
  };

  const openMapDrawer = (pedido) => {
    setMapDrawerData({
      paciente: pedido?.paciente || '',
      direccion: pedido?.direccion || '',
      medicamento: getMedicamentoCompleto(pedido),
      laboratorio: pedido?.laboratorio || '',
      latitud: Number.isFinite(Number(pedido?.latitud)) ? Number(pedido.latitud) : null,
      longitud: Number.isFinite(Number(pedido?.longitud)) ? Number(pedido.longitud) : null,
      nroPedido: pedido?.nro_pedido || ''
    });
    setMapDrawerOpen(true);
  };

  const swalConfirmZIndexFix = {
    allowOutsideClick: false,
    zIndex: 4000,
    didOpen: () => {
      const container = Swal.getContainer();
      if (container) container.style.zIndex = '4000';
      const popup = Swal.getPopup();
      if (popup) popup.style.zIndex = '4001';
    }
  };

  const ejecutarMarcarEntregado = async (pedido, origen = 'cadeteria') => {
    try {
      const esAtencion = isAtencionFarmaciaPedido(pedido);
      const pid = esAtencion ? resolveAtencionPedidoId(pedido) : Number(pedido?.id);
      if (!Number.isFinite(pid)) {
        await Swal.fire({
          icon: 'error',
          title: 'No se pudo actualizar',
          text: 'ID de pedido inválido.',
          customClass: { popup: 'swal-toast-crm' },
          zIndex: 4000
        });
        return false;
      }

      let updated = null;
      if (esAtencion) {
        // Pedidos de bot mostrador viven en atencion_pedidos: endpoint propio.
        const res = await updateAtencionPedidoFarmaciaEstado(pid, 'entregado', origen);
        updated = res?.pedido || res || null;
      } else {
        const r = await fetch(`${API_BASE_URL}/api/asociacion-recetas/pedidos-farmacia/${pid}/estado`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ new_estado: 'entregado', origen })
        });
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          throw new Error(err?.message || err?.error || `HTTP ${r.status}`);
        }
        updated = await r.json().catch(() => null);
      }

      setItems((prev) => {
        const list = Array.isArray(prev) ? prev : [];
        return list.map((it) => (
          String(it?.id) === String(pedido?.id)
            ? { ...it, ...(esAtencion ? {} : (updated || {})), estado: 'entregado' }
            : it
        ));
      });
      return true;
    } catch (e) {
      Swal.fire({
        icon: 'error',
        title: 'Error al actualizar',
        text: e?.message || 'No se pudo marcar como entregado.',
        customClass: { popup: 'swal-toast-crm' },
        zIndex: 4000
      });
      return false;
    }
  };

  const solicitarConfirmacionEntrega = async (pedido, origen = 'cadeteria_kanban') => {
    const estadoActual = String(pedido?.estado || '');
    if (estadoActual !== 'en_camino') {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'error',
        title: 'Transición no permitida',
        text: 'Solo podés confirmar entregas de pedidos en camino.',
        showConfirmButton: false,
        timer: 3200,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
      return false;
    }

    const confirmResult = await fireConfirmDialog({
      title: 'Confirmar entrega',
      html: buildCrmSwalBody({
        lead: `¿Confirmás que el pedido de <strong>${pedido?.paciente || 'este paciente'}</strong> fue entregado al paciente?`,
        details: [
          { label: 'Estado actual', value: cadeteriaEstadoLabel(estadoActual) },
          { label: 'Nuevo estado', value: cadeteriaEstadoLabel('entregado') },
          ...(pedido?.nro_pedido ? [{ label: 'N° pedido', value: String(pedido.nro_pedido) }] : [])
        ],
        items: ['Quedará registrado quién confirmó la entrega']
      }),
      icon: 'question',
      confirmButtonText: 'Confirmar entrega',
      cancelButtonText: 'Cancelar',
      ...swalConfirmZIndexFix
    });
    if (!confirmResult.isConfirmed) return false;
    return ejecutarMarcarEntregado(pedido, origen);
  };

  const marcarComoEntregado = async (pedido) => {
    const ok = await solicitarConfirmacionEntrega(pedido, 'cadeteria');
    if (ok) {
      Swal.fire({
        toast: true,
        position: 'bottom-end',
        icon: 'success',
        title: 'Pedido marcado como entregado',
        showConfirmButton: false,
        timer: 2200,
        timerProgressBar: true,
        customClass: { popup: 'swal-toast-crm' }
      });
    }
    return ok;
  };

  const formatDateTime = (raw) => {
    if (!raw) return 'Sin fecha';
    const source = String(raw);
    const normalized = source.replace(/(\.\d{3})\d+/, '$1').replace(' ', 'T');
    const d = new Date(normalized);
    if (!Number.isNaN(d.getTime())) {
      const argMs = d.getTime() - (3 * 60 * 60 * 1000);
      const argDate = new Date(argMs);
      const hh = String(argDate.getUTCHours()).padStart(2, '0');
      const mm = String(argDate.getUTCMinutes()).padStart(2, '0');
      return `${hh}:${mm} hs`;
    }
    const timeMatch = source.match(/(?:T|\s)(\d{2}):(\d{2})(?::\d{2})?/);
    if (timeMatch) {
      let hh = Number(timeMatch[1]);
      const mm = timeMatch[2];
      hh = (hh - 3 + 24) % 24;
      return `${String(hh).padStart(2, '0')}:${mm} hs`;
    }
    return source;
  };

  const formatDni = (raw) => {
    const digits = String(raw || '').replace(/\D/g, '');
    if (!digits) return '—';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const formatMedicamentoLabel = (raw) => {
    const name = String(raw || '').trim();
    if (!name) return '—';
    return name
      .split(' ')
      .filter(Boolean)
      .map((word) => {
        if (word.length <= 1) return word.toUpperCase();
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  };

  const hasMeaningfulValue = (rawValue) => {
    const value = String(rawValue || '').trim();
    if (!value) return false;
    return value !== '0';
  };
  const abrirInfoPendienteAnterior = (pedido) => {
    if (!pedido) return;
    setPendienteInfoPedido(pedido);
    setPendienteInfoDrawerOpen(true);
  };
  const pendienteDrawerFecha = formatFechaSoloDia(pendienteInfoPedido?.fecha);
  const pendienteDrawerPaciente = getPedidoPacienteDisplay(pendienteInfoPedido);
  const pendienteDrawerDni = formatDni(getPedidoDni(pendienteInfoPedido));
  const pendienteDrawerEstado = String(pendienteInfoPedido?.estado || '').toLowerCase() === 'en_camino' ? 'En camino' : 'Pendiente';
  const pendienteDrawerMedicamento = formatMedicamentoLabel(getMedicamentoCompleto(pendienteInfoPedido));
  const pendienteDrawerPresentacion = String(pendienteInfoPedido?.presentacion || pendienteInfoPedido?.concentracion || '—');
  const pendienteDrawerDireccion = String(pendienteInfoPedido?.direccion || 'Sin dirección');
  const pendienteDrawerNumeroPedido = String(pendienteInfoPedido?.nro_pedido || 'Sin N°');
  const pendienteDrawerNotas = String(pendienteInfoPedido?.notas || pendienteInfoPedido?.observaciones || 'Sin notas');

  const isMobile = viewportWidth <= 768;
  const mapDrawerPanelStyle = {
    display: 'flex',
    flexDirection: 'column'
  };
  const mapDrawerMapHeight = Math.max(viewportHeight - 210, 280);
  const drawerPanelStyle = isMobile
    ? { width: '100vw', minWidth: 0, maxWidth: '100vw' }
    : { width: '46vw', minWidth: 520, maxWidth: 760 };
  const historialPanelStyle = isMobile
    ? { width: '100vw', minWidth: 0, maxWidth: '100vw' }
    : { width: '50vw', minWidth: 560, maxWidth: 860 };
  if (permissionsLoading) {
    return (
      <section className="crm-gd-page crm-farm-page crm-cad-page crm-pbody">
        <div className="crm-gd-loading" role="status" aria-live="polite">
          <div className="crm-gd-spinner" aria-hidden="true" />
          <div className="crm-gd-loading__title">Cargando permisos</div>
        </div>
      </section>
    );
  }
  if (!hasPermission) return <CenteredMessage title="Acceso denegado" message="No tenés permiso para ver Pedidos" icon="🔒" />;

  return (
    <section className="crm-gd-page crm-farm-page crm-cad-page crm-pbody">
      <div className="crm-dispensa-chrome">
      <div className="crm-gd-kpi-grid crm-farm-kpi-grid crm-farm-kpi-strip--dispensa">
        <div className="crm-gd-kpi-card crm-farm-kpi-card--cad-camino">
          <div className="crm-gd-kpi-card__num">{kpi.enCamino}</div>
          <div className="crm-gd-kpi-card__label">En camino</div>
        </div>
        <div className="crm-gd-kpi-card crm-farm-kpi-card--cad-entregado">
          <div className="crm-gd-kpi-card__num">{kpi.entregadosHoy}</div>
          <div className="crm-gd-kpi-card__label">Entregados</div>
        </div>
        <div className="crm-gd-kpi-card crm-farm-kpi-card--cad-cancelado">
          <div className="crm-gd-kpi-card__num">{kpi.cancelados}</div>
          <div className="crm-gd-kpi-card__label">Cancelados</div>
        </div>
      </div>

      <div className="crm-farm-filters">
        <div className="crm-farm-filters__search-row">
          <input
            type="search"
            className="crm-gd-input crm-farm-filters__search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar paciente, medicamento, dirección o n° pedido"
            aria-label="Buscar pedidos"
          />
          <button
            type="button"
            className="crm-gd-btn crm-gd-btn--ghost crm-farm-filters__hist"
            onClick={() => {
              setHistorialGlobalDni('');
              setHistorialGlobalPacienteSearch('');
              setHistorialGlobalSearch('');
              setHistorialGlobalDesde('');
              setHistorialGlobalHasta('');
              setHistorialGlobalOrder('fecha_desc');
              setHistorialGlobalPage(1);
              setHistorialGlobalOpen(true);
            }}
          >
            Historial
          </button>
        </div>
        <div className="crm-farm-filters__controls">
          <div className="crm-farm-segment" role="group" aria-label="Vista">
            <button
              type="button"
              className={`crm-farm-segment__btn${filtroRangoModo === 'solo_hoy' ? ' is-active' : ''}`}
              onClick={() => setFiltroRangoModo('solo_hoy')}
            >
              Hoy
            </button>
            <button
              type="button"
              className={`crm-farm-segment__btn${filtroRangoModo === 'hoy_anteriores' ? ' is-active' : ''}`}
              onClick={() => setFiltroRangoModo('hoy_anteriores')}
            >
              Backlog
            </button>
          </div>
          <div className="crm-farm-date-range">
            <label className="crm-farm-date-range__field">
              <span className="crm-farm-date-range__lbl">Desde</span>
              <input type="date" className="crm-gd-input" value={filtroDesde} onChange={(e) => setFiltroDesde(e.target.value)} />
            </label>
            <label className="crm-farm-date-range__field">
              <span className="crm-farm-date-range__lbl">Hasta</span>
              <input type="date" className="crm-gd-input" value={filtroHasta} onChange={(e) => setFiltroHasta(e.target.value)} />
            </label>
          </div>
          <button
            type="button"
            className="crm-gd-btn crm-farm-filters__clear"
            onClick={() => {
              setSearch('');
              setFiltroDesde('');
              setFiltroHasta('');
              setFiltroRangoModo('hoy_anteriores');
            }}
          >
            Limpiar
          </button>
        </div>
      </div>
      </div>

      <div className="crm-farm-board-wrap" style={{ minHeight: Math.max(viewportHeight - 130, 280) }}>
        {loading ? (
          <div className="crm-gd-loading" role="status" aria-live="polite" aria-busy="true">
            <div className="crm-gd-spinner" aria-hidden="true" />
            <div className="crm-gd-loading__title">Preparando monitoreo de cadetería</div>
            <p style={{ margin: 0, fontSize: 12, color: '#64748b' }}>Cargando repartos en curso…</p>
          </div>
        ) : !isTabletOrLess ? (
          <div className="crm-farm-board crm-farm-board--cols-3">
            {CADETERIA_KANBAN_COLUMNS.map((col) => {
              const cards = col.key === 'en_camino' ? pageItems : (col.key === 'entregado' ? deliveredItems : cancelledItems);
              const isEntregadosColumn = col.key === 'entregado';
              const isReadOnlyColumn = isCadeteriaReadonlyColumn(col.key);
              const isDropZone = isCadeteriaDropZoneColumn(col.key);
              const isHistColumn = col.key === 'entregado' || col.key === 'cancelado';
              const colMeta = getCadeteriaColumnMeta(col.key);
              const isOver = dragOverColumn === col.key;
              return (
                <div
                  key={`cad-col-${col.key}`}
                  className={`crm-farm-kanban-col${isReadOnlyColumn ? ' is-readonly' : ''}${isEntregadosColumn ? ' is-finalizados' : ''}${isDropZone ? ' is-drop-zone' : ''}${isOver ? ' is-over' : ''}`}
                  aria-disabled={isReadOnlyColumn ? true : undefined}
                  title={isHistColumn ? colMeta.ariaTitle : undefined}
                  onDragOver={(e) => {
                    if (!col.dropEnabled) return;
                    e.preventDefault();
                    setDragOverColumn(col.key);
                  }}
                  onDragLeave={() => setDragOverColumn((prev) => (prev === col.key ? '' : prev))}
                  onDrop={async (e) => {
                    if (!col.dropEnabled) return;
                    e.preventDefault();
                    const draggedId = String(e.dataTransfer.getData('text/plain') || draggingPedidoId || '');
                    setDragOverColumn('');
                    setDraggingPedidoId(null);
                    if (!draggedId) return;
                    const pedido = pageItems.find((x) => String(x.id) === draggedId);
                    if (!pedido || pedido.estado === col.key) return;
                    const ok = await solicitarConfirmacionEntrega(pedido, 'cadeteria_kanban');
                    if (ok) {
                      Swal.fire({
                        toast: true,
                        position: 'bottom-end',
                        icon: 'success',
                        title: 'Movido a Entregados',
                        showConfirmButton: false,
                        timer: 1600,
                        customClass: { popup: 'swal-toast-crm' }
                      });
                    }
                  }}
                >
                  <div className="crm-farm-kanban-col__head">
                    <div>
                      <div className="crm-farm-kanban-col__title">{col.title}</div>
                      {col.subtitle ? (
                        <div className="crm-farm-kanban-col__sub">{col.subtitle}</div>
                      ) : null}
                    </div>
                    <div className="crm-farm-kanban-col__meta">
                      {isReadOnlyColumn ? (
                        <span className="crm-farm-kanban-col__lock">
                          <span className="crm-farm-kanban-col__lock-icon" aria-hidden>🔒</span>
                          Solo lectura
                        </span>
                      ) : null}
                      <span className="crm-farm-kanban-col__count">{cards.length}</span>
                    </div>
                  </div>
                  {isDropZone ? (
                    <div className="crm-cad-drop-banner" role="note">
                      <span className="crm-cad-drop-banner__icon" aria-hidden>{colMeta.icon}</span>
                      <div className="crm-cad-drop-banner__text">
                        <strong>{colMeta.title}</strong>
                        <span>{colMeta.hint}</span>
                      </div>
                    </div>
                  ) : null}
                  {isReadOnlyColumn ? (
                    <div className="crm-farm-readonly-banner" role="note">
                      <span className="crm-farm-readonly-banner__icon" aria-hidden>{colMeta.icon}</span>
                      <div className="crm-farm-readonly-banner__text">
                        <strong>{colMeta.title}</strong>
                        <span>{colMeta.hint}</span>
                      </div>
                    </div>
                  ) : null}
                  <div className="crm-farm-kanban-col__cards">
                    {cards.length === 0 ? (
                      <div className={`crm-farm-kanban-empty${isDropZone ? ' crm-farm-kanban-empty--drop' : ''}${isHistColumn && !isDropZone ? ' crm-farm-kanban-empty--readonly' : ''}`}>
                        {isDropZone ? (
                          <>
                            <span className="crm-farm-kanban-empty__ico" aria-hidden>✅</span>
                            Soltá pedidos aquí
                            <span className="crm-farm-kanban-empty__hint">{colMeta.emptyHint}</span>
                          </>
                        ) : isHistColumn ? (
                          <>
                            <span className="crm-farm-kanban-empty__ico" aria-hidden>📋</span>
                            {colMeta.empty}
                            <span className="crm-farm-kanban-empty__hint">{colMeta.emptyHint}</span>
                          </>
                        ) : (
                          'Sin repartos en curso'
                        )}
                      </div>
                    ) : cards.map((p) => {
                      const category = inferMedicationCategory(getMedicamentoCompleto(p));
                      const isPendienteAnterior = col.key === 'en_camino' && isPedidoPendienteAnterior(p);
                      const isFinalizadoCard = isHistColumn && isPedidoEstadoTerminal(p.estado);
                      const finalizadoVariant = getFinalizadoCardVariant(p.estado);
                      const isDraggingCard = String(draggingPedidoId) === String(p.id);
                      return (
                        <div
                          key={`cad-k-${col.key}-${p.id}`}
                          draggable={col.key === 'en_camino'}
                          onDragStart={(e) => {
                            if (col.key !== 'en_camino') return;
                            setDraggingPedidoId(String(p.id));
                            e.dataTransfer.setData('text/plain', String(p.id));
                            e.dataTransfer.effectAllowed = 'move';
                          }}
                          onDragEnd={() => {
                            setDraggingPedidoId(null);
                            setDragOverColumn('');
                          }}
                          className={`crm-farm-kanban-card${isFinalizadoCard ? ` is-finalizado is-finalizado--${finalizadoVariant}` : ''}${isPendienteAnterior ? ' is-backlog' : ''}${isDraggingCard ? ' is-dragging' : ''}`}
                        >
                          {isFinalizadoCard ? (
                            <>
                              <div className="crm-farm-finalizado-ribbon" aria-hidden />
                              <div className="crm-farm-finalizado-stamp" aria-hidden>
                                <span className="crm-farm-finalizado-stamp__ring" />
                                <span className="crm-farm-finalizado-stamp__icon">{finalizadoVariant === 'cancelado' ? '✕' : '✓'}</span>
                                <span className="crm-farm-finalizado-stamp__label">{getCadeteriaFinalizadoStampLabel(p.estado)}</span>
                              </div>
                            </>
                          ) : null}
                          {isPendienteAnterior ? (
                            <button
                              type="button"
                              className="crm-farm-backlog-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                abrirInfoPendienteAnterior(p);
                              }}
                              title="Ver detalle de pendiente anterior"
                              aria-label="Ver pendiente anterior"
                            >
                              i
                            </button>
                          ) : null}
                          <div className="crm-farm-kanban-card__body">
                            <div className={`crm-farm-kanban-card__top${isFinalizadoCard ? ' crm-farm-kanban-card__top--readonly' : ''}`}>
                              <div className="crm-farm-kanban-card__contact">
                                <div className="crm-farm-kanban-card__patient" title={p.paciente || 'Paciente'}>
                                  {p.paciente || 'Paciente'}
                                </div>
                                <div className="crm-disp-card-tags">
                                  <span
                                    className="crm-farm-kanban-card__estado"
                                    style={{ background: category.tint, color: category.text }}
                                  >
                                    <span aria-hidden>{category.emoji}</span> {category.label}
                                  </span>
                                </div>
                              </div>
                              {col.key === 'en_camino' ? (
                                <button
                                  type="button"
                                  className="crm-cad-kanban-card__map-emoji-btn"
                                  draggable={false}
                                  onDragStart={(e) => e.stopPropagation()}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openMapDrawer(p);
                                  }}
                                  title="Ver mapa de entrega"
                                  aria-label="Ver mapa de entrega"
                                >
                                  🗺️
                                </button>
                              ) : null}
                            </div>
                            <div className="crm-disp-card-med-block">
                              <div className="crm-farm-kanban-card__med">{formatMedicamentoLabel(getMedicamentoCompleto(p))}</div>
                              {(p.presentacion || p.concentracion) ? (
                                <div className="crm-farm-kanban-card__presentacion">{String(p.presentacion || p.concentracion)}</div>
                              ) : null}
                            </div>
                            <div className="crm-disp-card-foot">
                              <div className="crm-farm-kanban-card__addr">{p.direccion || 'Sin dirección'}</div>
                              <div className="crm-farm-kanban-card__nro">
                                DNI {formatDni(p.cliente_dni)} · {p.nro_pedido ? `N° ${p.nro_pedido}` : 'Sin N° pedido'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="crm-cad-mobile-queue">
            <div className="crm-cad-mobile-queue__head">
              <span className="crm-cad-mobile-queue__head-icon" aria-hidden>🏍️</span>
              Cola de repartos en curso
            </div>
              <div style={{ maxHeight: isTabletOrLess ? 'none' : 'calc(100vh - 86px)', overflowY: 'auto' }}>
              {pageItems.map((p) => {
                const active = String(selectedId) === String(p.id);
                return (
                  <div key={p.id} style={{ borderBottom: '1px solid rgba(148,163,184,.12)' }}>
                    <button
                      type="button"
                      onClick={() => handleSelectPedido(active ? null : p.id)}
                      style={{
                        width: '100%',
                        textAlign: 'left',
                        border: 'none',
                        background: active ? '#EEF4FB' : 'transparent',
                        color: '#0f172a',
                        padding: '11px 12px',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'center' }}>
                        <div style={{ fontWeight: 800, fontSize: 14 }}>{p.paciente || 'Paciente'}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10, fontWeight: 800, borderRadius: 999, padding: '3px 8px', background: '#EEF4FB', color: '#1C4F86', border: '1px solid #D4E1F1' }}>EN CAMINO</span>
                          <span
                            aria-hidden="true"
                            style={{
                              border: '1px solid rgba(148,163,184,.25)',
                              background: '#fff',
                              width: 30,
                              height: 30,
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              borderRadius: 999,
                              transition: 'transform .28s cubic-bezier(.2,.9,.2,1)',
                              boxShadow: '0 2px 8px rgba(15,23,42,.12)',
                              color: '#0f172a'
                            }}
                          >
                            {active ? (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', opacity: 0.95 }}>
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6z" fill="currentColor" />
                              </svg>
                            ) : (
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', opacity: 0.95 }}>
                                <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6z" fill="currentColor" />
                              </svg>
                            )}
                          </span>
                        </div>
                      </div>
                      <div style={{ marginTop: 4, fontSize: 11, color: '#64748b' }}>DNI {formatDni(p.cliente_dni)} · {p.nro_pedido || 'Sin N°'}</div>
                      <div style={{ marginTop: 4, fontSize: 11, color: '#334155', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.direccion || 'Sin dirección'}</div>
                      <div style={{ marginTop: 4, fontSize: 10, color: '#64748b' }}>{formatDateTime(p.fecha)}</div>
                    </button>
                    <div
                      style={{
                        maxHeight: active ? 520 : 0,
                        opacity: active ? 1 : 0,
                        overflow: 'hidden',
                        transition: 'max-height .36s cubic-bezier(.22,1,.36,1), opacity .24s ease',
                        background: '#F8FAFC'
                      }}
                    >
                      <div ref={active ? detailPanelRef : null} style={{ padding: 10, display: 'grid', gap: 8 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center' }}>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 900, color: '#0f172a' }}>{p.paciente || 'Paciente'}</div>
                            <div style={{ fontSize: 12, color: '#64748b' }}>Pedido {p.nro_pedido || '—'} · DNI {formatDni(p.cliente_dni)}</div>
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 800, borderRadius: 999, padding: '4px 10px', background: '#EEF4FB', color: '#1C4F86', border: '1px solid #D4E1F1' }}>
                            Misión activa
                          </span>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isTabletOrLess ? '1fr' : 'repeat(3,minmax(0,1fr))', gap: 8 }}>
                          <div style={{ borderRadius: 10, border: '1px solid rgba(148,163,184,.22)', background: '#ffffff', padding: 9 }}>
                            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800 }}>Medicamento</div>
                            <div style={{ color: '#0f172a', marginTop: 4, fontWeight: 800 }}>{formatMedicamentoLabel(getMedicamentoCompleto(p))}</div>
                          </div>
                          {hasMeaningfulValue(p.laboratorio) ? (
                            <div style={{ borderRadius: 10, border: '1px solid rgba(148,163,184,.22)', background: '#ffffff', padding: 9 }}>
                              <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800 }}>Laboratorio</div>
                              <div style={{ color: '#0f172a', marginTop: 4, fontWeight: 700 }}>{p.laboratorio}</div>
                            </div>
                          ) : null}
                          <div style={{ borderRadius: 10, border: '1px solid rgba(148,163,184,.22)', background: '#ffffff', padding: 9 }}>
                            <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800 }}>Salida</div>
                            <div style={{ color: '#0f172a', marginTop: 4, fontWeight: 700 }}>{formatDateTime(p.fecha)}</div>
                          </div>
                        </div>
                        <div style={{ borderRadius: 10, border: '1px solid rgba(148,163,184,.22)', background: '#ffffff', padding: 9 }}>
                          <div style={{ fontSize: 10, color: '#64748b', fontWeight: 800 }}>Dirección de entrega</div>
                          <div style={{ color: '#0f172a', marginTop: 4, fontWeight: 700 }}>{p.direccion || 'Sin dirección'}</div>
                        </div>
                        <div style={{ display: 'flex', justifyContent: isTabletOrLess ? 'stretch' : 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="crm-cad-kanban-card__map-emoji-btn crm-cad-kanban-card__map-emoji-btn--mobile"
                            onClick={() => openMapDrawer(p)}
                            title="Ver mapa de entrega"
                            aria-label="Ver mapa de entrega"
                          >
                            🗺️
                          </button>
                          <button
                            type="button"
                            className="crm-sb-btn"
                            onClick={() => marcarComoEntregado(p)}
                            style={{
                              borderRadius: 999,
                              border: '1px solid #BDE9E4',
                              background: '#E5F8F6',
                              color: '#00796B',
                              fontWeight: 700,
                              padding: '7px 12px',
                              width: isTabletOrLess ? '100%' : 'auto'
                            }}
                          >
                            Marcar entregado
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div
        role="presentation"
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right${pendienteInfoDrawerOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setPendienteInfoDrawerOpen(false);
        }}
      >
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Detalle de pendiente anterior"
          className={`crm-gd-drawer-panel${pendienteInfoDrawerOpen ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'}`}
          style={drawerPanelStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crm-gd-drawer-head">
            <div>
              <div className="crm-gd-drawer-head__title">Pendiente anterior</div>
              <p className="crm-gd-drawer-head__sub">Detalle del pedido para seguimiento operativo.</p>
            </div>
            <button type="button" className="crm-gd-btn" onClick={() => setPendienteInfoDrawerOpen(false)}>Cerrar</button>
          </div>
          <div className="crm-gd-drawer-body">
            <div className="crm-farm-alert-banner">
              <div className="crm-farm-alert-banner__chip">Seguimiento prioritario</div>
              <p className="crm-farm-alert-banner__text">
                Este pedido quedó pendiente del día <strong>{pendienteDrawerFecha}</strong> y requiere seguimiento prioritario.
              </p>
            </div>
            <div className="crm-gd-med-box" style={{ marginTop: 12 }}>
              <dl className="crm-farm-detail-grid">
                <dt>Paciente</dt><dd>{pendienteDrawerPaciente}</dd>
                <dt>DNI</dt><dd>{pendienteDrawerDni}</dd>
                <dt>Estado</dt><dd>{pendienteDrawerEstado}</dd>
                <dt>Fecha pedido</dt><dd>{pendienteDrawerFecha}</dd>
                <dt>Medicamento</dt><dd>{pendienteDrawerMedicamento}</dd>
                <dt>Presentación</dt><dd>{pendienteDrawerPresentacion}</dd>
                <dt>Dirección</dt><dd>{pendienteDrawerDireccion}</dd>
                <dt>N° pedido</dt><dd>{pendienteDrawerNumeroPedido}</dd>
                <dt>Notas</dt><dd>{pendienteDrawerNotas}</dd>
              </dl>
            </div>
            <div className="crm-gd-med-box" style={{ marginTop: 10 }}>
              <div className="crm-gd-field-label">Mapa del reparto</div>
              <div style={{ borderRadius: 12, overflow: 'hidden', border: '1px solid rgba(148,163,184,.22)', minHeight: 220, background: '#020617', marginTop: 8 }}>
                <React.Suspense fallback={<div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 12 }}>Cargando mapa…</div>}>
                  <DeliveryMap
                    address={pendienteInfoPedido?.direccion}
                    latitude={Number.isFinite(Number(pendienteInfoPedido?.latitud)) ? Number(pendienteInfoPedido?.latitud) : null}
                    longitude={Number.isFinite(Number(pendienteInfoPedido?.longitud)) ? Number(pendienteInfoPedido?.longitud) : null}
                    height={220}
                    interactive={false}
                  />
                </React.Suspense>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {mapDrawerOpen && (
        <div
          role="presentation"
          className="crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right crm-gd-drawer-backdrop--open"
          onClick={() => setMapDrawerOpen(false)}
        >
          <aside
            role="dialog"
            aria-modal="true"
            aria-label="Mapa de entrega"
            className="crm-gd-drawer-panel crm-gd-drawer-panel--right-open crm-cad-map-drawer"
            style={mapDrawerPanelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="crm-gd-drawer-head">
              <div>
                <div className="crm-gd-drawer-head__title">{mapDrawerData.paciente || 'Mapa de entrega'}</div>
                <p className="crm-gd-drawer-head__sub">
                  Pedido {mapDrawerData.nroPedido || '—'} · {formatMedicamentoLabel(mapDrawerData.medicamento)}
                </p>
              </div>
              <button type="button" className="crm-gd-btn" onClick={() => setMapDrawerOpen(false)}>Cerrar</button>
            </div>
            <div className="crm-cad-map-drawer__stats">
              <div className="crm-gd-med-box">
                <div className="crm-gd-field-label">Medicamento</div>
                <div className="crm-gd-field-value">{formatMedicamentoLabel(mapDrawerData.medicamento)}</div>
              </div>
              {hasMeaningfulValue(mapDrawerData.laboratorio) ? (
                <div className="crm-gd-med-box">
                  <div className="crm-gd-field-label">Laboratorio</div>
                  <div className="crm-gd-field-value">{mapDrawerData.laboratorio}</div>
                </div>
              ) : (
                <div className="crm-gd-med-box">
                  <div className="crm-gd-field-label">Reparto</div>
                  <div className="crm-gd-field-value">🏍️ Última milla</div>
                </div>
              )}
              <div className="crm-gd-med-box">
                <div className="crm-gd-field-label">Dirección</div>
                <div className="crm-gd-field-value">{mapDrawerData.direccion || 'Sin dirección'}</div>
              </div>
            </div>
            <div className="crm-cad-map-drawer__map-wrap">
              <div className="crm-cad-map-drawer__map-inner">
                <React.Suspense fallback={<div style={{ height: mapDrawerMapHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>Cargando mapa completo…</div>}>
                  <DeliveryMap
                    address={mapDrawerData.direccion}
                    latitude={mapDrawerData.latitud}
                    longitude={mapDrawerData.longitud}
                    height={mapDrawerMapHeight}
                    interactive
                  />
                </React.Suspense>
              </div>
            </div>
          </aside>
        </div>
      )}

      <div
        role="presentation"
        className={`crm-gd-drawer-backdrop crm-gd-drawer-backdrop--right${historialGlobalOpen ? ' crm-gd-drawer-backdrop--open' : ' crm-gd-drawer-backdrop--closed'}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) setHistorialGlobalOpen(false);
        }}
      >
        <aside
          role="dialog"
          aria-modal="true"
          aria-label="Historial global de cadetería"
          className={`crm-gd-drawer-panel${historialGlobalOpen ? ' crm-gd-drawer-panel--right-open' : ' crm-gd-drawer-panel--right-closed'}`}
          style={historialPanelStyle}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="crm-gd-drawer-head">
            <div>
              <div className="crm-gd-drawer-head__title">Historial de cadetería</div>
              <p className="crm-gd-drawer-head__sub">Pedidos entregados o cancelados, fuera del tablero activo.</p>
            </div>
            <button type="button" className="crm-gd-btn" onClick={() => setHistorialGlobalOpen(false)}>Cerrar</button>
          </div>
          <div className="crm-gd-drawer-body">
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.1fr 1fr', gap: 10, alignItems: 'end', marginBottom: 10 }}>
              <div>
                <label className="crm-gd-field-label" htmlFor="cad-hist-pac-search">Paciente (DNI)</label>
                <input id="cad-hist-pac-search" className="crm-gd-input" value={historialGlobalPacienteSearch} onChange={(e) => setHistorialGlobalPacienteSearch(e.target.value)} placeholder="Buscar paciente en el selector…" />
              </div>
              <div>
                <label className="crm-gd-field-label" htmlFor="cad-hist-pac-select">Selección</label>
                <select id="cad-hist-pac-select" className="crm-gd-select" value={historialGlobalDni} onChange={(e) => setHistorialGlobalDni(e.target.value)}>
                  <option value="">Seleccioná un paciente</option>
                  {filteredHistorialPacientes.map((p) => (
                    <option key={`hgc-${p.dni}`} value={p.dni}>{`${p.nombre} · DNI ${formatDni(p.dni)}`}</option>
                  ))}
                </select>
              </div>
            </div>
            <p style={{ margin: '0 0 10px', fontSize: 12, color: '#475569', fontWeight: 700 }}>{filteredHistorialGlobalItems.length} registros</p>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr 1fr 0.7fr', gap: 8, marginBottom: 12, alignItems: 'end' }}>
              <div>
                <label className="crm-gd-field-label" htmlFor="cad-hist-search">Buscar</label>
                <input id="cad-hist-search" className="crm-gd-input" value={historialGlobalSearch} onChange={(e) => setHistorialGlobalSearch(e.target.value)} placeholder="Medicamento, nro o dirección…" disabled={!historialGlobalDni} />
              </div>
              <div>
                <label className="crm-gd-field-label" htmlFor="cad-hist-desde">Desde</label>
                <input id="cad-hist-desde" type="date" className="crm-gd-input" value={historialGlobalDesde} onChange={(e) => setHistorialGlobalDesde(e.target.value)} disabled={!historialGlobalDni} />
              </div>
              <div>
                <label className="crm-gd-field-label" htmlFor="cad-hist-hasta">Hasta</label>
                <input id="cad-hist-hasta" type="date" className="crm-gd-input" value={historialGlobalHasta} onChange={(e) => setHistorialGlobalHasta(e.target.value)} disabled={!historialGlobalDni} />
              </div>
              <div>
                <label className="crm-gd-field-label" htmlFor="cad-hist-order">Orden</label>
                <select id="cad-hist-order" className="crm-gd-select" value={historialGlobalOrder} onChange={(e) => setHistorialGlobalOrder(e.target.value)} disabled={!historialGlobalDni}>
                  <option value="fecha_desc">Más reciente</option>
                  <option value="fecha_asc">Más antiguo</option>
                </select>
              </div>
              <div>
                <label className="crm-gd-field-label" htmlFor="cad-hist-page-size">Mostrar</label>
                <select id="cad-hist-page-size" className="crm-gd-select" value={historialGlobalItemsPerPage} onChange={(e) => setHistorialGlobalItemsPerPage(Number(e.target.value))} disabled={!historialGlobalDni}>
                  <option value={6}>6</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
              </div>
            </div>
            {!historialGlobalDni ? (
              <div className="crm-gd-empty">
                <span aria-hidden style={{ fontSize: 26 }}>🗂️</span>
                <div style={{ fontWeight: 700, marginTop: 6 }}>Sin historial para mostrar</div>
                <p style={{ margin: '4px 0 0', fontSize: 13 }}>Seleccioná un paciente para ver entregas y cancelaciones.</p>
              </div>
            ) : pageHistorialGlobalItems.length === 0 ? (
              <div className="crm-gd-empty">
                <span aria-hidden style={{ fontSize: 26 }}>🗂️</span>
                <div style={{ fontWeight: 700, marginTop: 6 }}>Sin resultados</div>
                <p style={{ margin: '4px 0 0', fontSize: 13 }}>No hay pedidos con esos filtros.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {pageHistorialGlobalItems.map((p) => (
                  <div key={`hgc-row-${p.id}`} className="crm-gd-card" style={{ padding: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, flexWrap: 'wrap' }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: '#0f172a' }}>{formatMedicamentoLabel(getMedicamentoCompleto(p))}</div>
                      <span className="crm-farm-status" style={cadeteriaEstadoStatusStyle(p.estado)}>
                        {cadeteriaEstadoLabel(p.estado)}
                      </span>
                    </div>
                    <div style={{ marginTop: 6, fontSize: 12, color: '#33475b', fontWeight: 700 }}>
                      {getPedidoPacienteDisplay(p)}{getPedidoDni(p) ? ` · DNI ${formatDni(getPedidoDni(p))}` : ''}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#64748b' }}>{p.nro_pedido ? `N° ${p.nro_pedido}` : 'Sin N° pedido'}</div>
                    {p.direccion ? <div style={{ marginTop: 6, fontSize: 12, color: '#516f90' }}>{p.direccion}</div> : null}
                    {p.fecha ? (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#64748b', fontWeight: 700 }}>
                        {cadeteriaEstadoLabel(p.estado)}: {formatDateTime(p.fecha)}
                      </div>
                    ) : null}
                  </div>
                ))}
                <div className="crm-farm-mobile-pagination">
                  <button type="button" className="crm-gd-btn" disabled={historialGlobalPage <= 1} onClick={() => setHistorialGlobalPage((prev) => Math.max(1, prev - 1))}>◀</button>
                  <span style={{ fontSize: 12, color: '#475569', fontWeight: 700 }}>
                    Página {historialGlobalPage} / {totalHistorialGlobalPages}
                  </span>
                  <button type="button" className="crm-gd-btn" disabled={historialGlobalPage >= totalHistorialGlobalPages} onClick={() => setHistorialGlobalPage((prev) => Math.min(totalHistorialGlobalPages, prev + 1))}>▶</button>
                </div>
              </div>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

export default Pedidos;